import React from 'react';

import {GanttApp} from '#components/gantt/GanttApp';

const articles = {
  "covidTracking": {
    "media": [
      {
        description: "Heatmap path tracking on a very inactive day",
        format: "png",
        path: "/covid-app/heatmap-point.png",
        type: "image",
      },
      {
        description: "Heatmap path tracking on a more active day",
        format: "png",
        path: "/covid-app/heatmap.png",
        type: "image",
      },
      {
        description: "Line path tracking",
        format: "png",
        path: "/covid-app/linemap.png",
        type: "image",
      },
      {
        description: "Showing off a variety of features of the prototype",
        format: "mp4",
        path: "/covid-app/options.mp4",
        type: "video",
      },
      {
        description: "Path options",
        format: "mp4",
        path: "/covid-app/path-options.mp4",
        type: "video",
      },
    ],
    "thumbnail": "/covid-app/heatmap-point.thumbnail.png",
    "rationale": "COVID is a potentially life-threatening disease, and reducing the number of concurrent cases can further deaths by overwhelming medical resources. Tracking citizens' movements is not very democratic, and presents its own set of issues, but can benefit greatly in identifying potential risk of infection and thereby can save lives through pre-emptive quarantining. ",
    "brief": "A solution could be a mobile application to track the location of an individual client-side. Data can be anonymised and used to discover intersections with known cases, and localised device connectivity (e.g., Bluetooth) could identify close-range interactions between devices. Homomorphic encryption could be used to perform calculations upon encrypted data, potentially allowing clients to communicate intersections of location information between one another without decrypting sensitive data.",
    "problems": "The feasibility for having a phone constantly track its location is a significant battery drain, making such an application practically unusable. Short-distance communication between mobiles as described above seemed somewhere between arduous and impossible at the application level. Homomorphic encryption as described above seemed feasible, but the learning curve was extremely steep considering I had no experience with the technology, and a quick release for the application was paramount.",
    "solution": "Unfortunately, I couldn't move past the location tracking issues described above and the project was abandoned a few weeks after beginning the prototype.",
  },
  "ubuntuTheming": {
    "media": [
      {
        description: "Before: Ubuntu's default theme",
        format: "png",
        path: "/ubuntu-theming/before.png",
        type: "image",
      },
      {
        description: "After: Ubuntu with the Nimix dark theme",
        format: "png",
        path: "/ubuntu-theming/after-dark.png",
        type: "image",
      },
      {
        description: "After: Ubuntu with the Nimix light theme",
        format: "png",
        path: "/ubuntu-theming/after-light.png",
        type: "image",
      },
      {
        description: "Full screen tmux status bar - note that the status bar is exchanged between the window bar and the first row of the terminal",
        format: "webm",
        path: "/ubuntu-theming/terminal-resize.webm",
        type: "video",
      },
      {
        description: "Full start up, including the plymouth splash screen",
        format: "webm",
        path: "/ubuntu-theming/startup.webm",
        type: "video",
      },
    ],
    "thumbnail": "/ubuntu-theming/after-dark.thumbnail.png",
    "rationale": "Ubuntu, and Unix systems generally, aren't always so visually pleasing. I like pretty software!",
    "brief": "Adding a Ubuntu theme is the way to go for changing the appearance of the OS and its applications.",
    "problems": "Ubuntu's theming process is pretty poorly documented, and discovering how to find the correct selectors for Gnome's theme CSS was a real challenge.",
    "solutions": " Fortunately there is a debugging tool, the GTK+ Inspector, although even with this tool I found myself having to dig through other themes to find out how to modify certain window aspects, and this is what I would recommend to others thinking of developing their own theme",
    "issues": "When modifying the boot screen, I discovered how simple it was to break the boot process with misconfigured CSS. Always use a VM to test this stuff!",
  },
  "cambridgeCalls": {
    "media": [
      {
        description: "Development of the mobile application",
        format: "mp4",
        path: "/cambridge-calls/mobile-app.mp4",
        type: "video",
      },
      {
        description: "In call using the website",
        format: "png",
        path: "/cambridge-calls/in-call.png",
        type: "image",
      },
      {
        description: "Demonstration call using the mobile application",
        format: "mp4",
        path: "/cambridge-calls/app-in-call.mp4",
        type: "video",
      },
    ],
    "thumbnail": "/cambridge-calls/in-call.thumbnail.png",
    "rationale": "During the COVID pandemic, university students became unable to socialise with their peers. For a friend at Cambridge University, this reduced the value of his time at the university significantly.",
    "brief": "We decided that a video networking application for authorised students would provide a decent supplement for providing this value during the pandemic. The application should connect other students randomly, providing a similar dynamic to a networking event. ",
    "problems": "Similar applications such as Omegle and ChatRoulette, suffer from extreme inappropriate behaviour where individuals commonly perform lewd acts from verbal assaults to physically exposing themselves, significantly reducing the experience for the user.",
    "solution": "Authorisation would help remove many of the usability pitfalls described above, alongside identifiable reporting features to help capture and hold accountable any inappropriate behaviour.",
    "issues": "Network effects made the application often empty of people to connect to, and we sought to solve this via funding to hire students from the university to remain on the application, and by expanding the user base further to other universities, under the name UniCalls.",
  },
  "distanceSensor": {
    "media": [
      {
        description: "The prototype distance sensor with its 3d-printed enclosure; banana for scale",
        format: "jpg",
        path: "/distance-sensor/banana-1.jpg",
        type: "image",
      },
      {
        description: "The prototype distance sensor with its 3d-printed enclosure; banana for scale",
        format: "jpg",
        path: "/distance-sensor/banana-2.jpg",
        type: "image",
      },
      {
        description: "The close-up of the prototype distance sensor with its 3d-printed enclosure",
        format: "jpg",
        path: "/distance-sensor/close-up.jpg",
        type: "image",
      },
      {
        description: "A marked diagram of the breadboard prototype",
        format: "jpg",
        path: "/distance-sensor/prototype.jpg",
        type: "image",
      },
    ], 
    "thumbnail": "/distance-sensor/banana-1.thumbnail.jpg",        
    "rationale": "Small distance (>100cm) measurements can be utilized for tracking the capacity of containers and cabinets. This project was used for an office environment, where floor space can be as costly as £1,000/m^2 (~$130/ft^2) per year in London, which is the approximate footprint of a standard cabinet. The cost of redundant space can quickly amount to a significant portion of a company's facilities expenditure, and a solution to reduce and consolidate empty storage can result in significant savings.",
    "brief": "One solution to recognising empty storage space is an automated sensor per cabinet compartment. Placing two distance measurement sensors on both walls of the compartment can give a decent approximation of the utility of the space. The device can emit these measurements to a server for data processing.",
    "problems": "The most significant problem to the proposed solution is the metal enclosure of a cabinet. Essentially acting as a Faraday cage, a closed cabinet will significantly attenuate any RF emission from the device. The battery life of the device can also present issues, compounded by the inability to emit a notification signal through the enclosure when the battery is low. The device must be as thin as possible so that it doesn't reduce the capacity of the compartment. Cost per device should be low enough to be outweighed by facility costs.",
    "solution": "Addressing the concerns of battery life and attenuation, a phototransistor provides a passive component with low draw that can reasonably identify if an enclosure is open. Once opened, measurements can be taken relatively frequently (~1s) with a small (SMD!) laser, such as VL53L0X which benefits from a surprisingly low draw (10mA) and transmitted using BLE to a server via beacon intermediary. An AVR MCU provides the brains with very low draw and cost. Further power reductions can be achieved via software by taking less frequent measurements in the case of frequently opened storage. Powered by a coin battery with ~250mAh, the device should theoretically last a few years before losing power. The photo- method restricts use of the sensor to enclosed storage; otherwise the frequent laser measurements quickly draw down the battery. This is addressed with a separate mode that takes periodic measurements.",
    "issues": "ToF lasers have relatively inaccurate measurements, off by as much as 10cm, depending on the reflectivity of the target. Fortunately this level of accuracy is nominal for our use case.",
  },
  "jumpcut": {
    "media": [
      {
        description: "The extension within Premiere Pro",
        format: "png",
        path: "/jumpcut/demo.PNG",
        type: "image",
      },
      {
        description: "A video showing most of the functionality of the extension",
        format: "mp4",
        path: "/jumpcut/example.mp4",
        type: "video",
      },
    ],
    "thumbnail": "/jumpcut/demo.thumbnail.PNG",        
    "rationale": "The bulk of video editing for vlog-style videos is spent upon sound adjustment and dead space removal, known as jumpcutting. One of the leading video editing products is Adobe Premiere Pro, which does not provide an automated solution for this task.",
    "brief": "A solution to provide this functionality can be realised by creating a Premiere Pro extension. Using OpenCV, it is a trivial task to slice the video audio stream and identify ranges below a relative or absolute threshold and present those to the user to make decisions.",
    "problems": "No significant problems were present for achieving the functionality, although Adobe's extension environment relied on a Javascript-like language called ExtendScript, which was cumbersome to debug and sparsely documented.",
    "solution": "An extension provides an input form for the video file, and adjustable parameters for configuring the jumpcutting, such as relative sound thresholds (including an upper bound for removing loud sounds). Output is divided into two columns of checkboxes, representing media clips above and below the thresholds, with a checkbox to toggle selection of all clips in the column. Toggling removes the associated clip(s), compressing the empty space.",
    "issues": "Three columns for below, within and above range clips would improve usability.",
  },
  "jobScraper": {
    "media": [
      {
        description: "An example job strategy for an unnamed job site",
        format: "png",
        path: "/job-hunting/job-strategy.png",
        type: "image",
      },
      {
        description: "Job scraping in action",
        format: "png",
        path: "/job-hunting/job-scraper.png",
        type: "image",
      },
      {
        description: "The results viewer",
        format: "png",
        path: "/job-hunting/job-viewer.png",
        type: "image",
      },
    ],
    "thumbnail": "/job-hunting/job-scraper.thumbnail.png",
    "rationale": "Job hunting can be frustrating. Many job sites make it challenging to find jobs in relevant sectors, and many do not collate all compensation, such that ordering by highest paid job does not necessariily guarantee that it includes eg bonus. Poor filtering and searching presents a challenge in finding the most suitable jobs, and a lack of compensation normalisation makes it difficult to know where the job stands relative to other jobs",
    "brief": "Standardising the data makes for a uniform experience, and normalising aggregations such as compensation between a normalised range gives a clearer picture of where compensation lies relative to other available jobs. Filtering for inclusion and exclusion for specific keywords, and excluding previously applied job IDs, helps reduce noise. Adding a metadata file to demark the first found job ID and time of scrape allows for daily scraping with short circuits for returning early from a keyword search when the previous job was hit, saving me time and the job site's bandwidth",
    "problems": "Some job sites severely (and sensibly) restrict scraping. This comes in the form of rate limiting, and CAPTCHAs",
    "solutions": "Delaying requests was the way to go to prevent rate limiting from impeding the scraper, with an exponentially delayed retry in the case of 403s. Whilst it's pretty simple to overcome CAPTCHAs with an online API service, this adds cost so I skipped out on adding the feature",
    "issues": "Some of the job sites changed over the time of my job hunt, so the strategies had to be updated.",
    "improvements": "The CAPTCHA API would have been useful, and improving the location feature with a radius of distance from a location would have increased the utility of the viewer"
  },
  "score": {
    "media": [
      {
        description: "A couple of example function calls from score packages",
        format: "png",
        path: "/score.sh/exemplar-calls.png",
        type: "image",
      },
      {
        description: "The https://score.sh website - check it out!",
        format: "png",
        path: "/score.sh/website.png",
        type: "image",
      },
    ],
    "thumbnail": "/score.sh/exemplar-calls.thumbnail.png",
    "rationale": "The shell scripting language is ubiquitous amongst Unix systems, but it syntactically cumbersome, slow, highly platform-dependent and non-portable, and the availability of scripts is sparse.",
    "brief": "score.sh aims to provide package management for shell scripts, as well as a basis of packages that provide core functionality in a performant, high usable, and ideally environment-agnostic way. Shell scripts are not distributed in a consistent manner (generally through Github or through vendor-specific websites) and there is no common solution for doing so. They are generally non-portable, poorly performant, and environment-dependent.",
    "problems": "Shell is a difficult language, and some tasks such as string manipulation are difficult to achieve without subshells, meaning violating DRY principles is mandatory. Some features, such as block-scoped variables and arrays, simply do not exist in portable shell.",
    "solution": "The core packages provide simple, interoperable methods to perform common tasks, such as parsing argument flags (e.g., `command --flag value`). There are packages provided to identify key variations amongst systems, allowing developers to produce scripts that execute environment-agnostically (e.g., `apps install` may identify the Unix OS flavour, abstract away the environment-specific install manager, and implement the specific install command - that is, `apps install X` may run e.g. `apt install X` for Debian, `pacman -S X` for Arch, etc.). These packages are highly performant relative to most shell scripts by avoiding the use of many of the performance-poor features of the shell language, especially subshells, and make use of posix to further interoperability and portability.",
    "issues": "This is a considerably sized project, and so documentation and testing has been lower priority than creating and releasing features. Originally the project began from a set of scripts, logically separated at first as functions in a single file, then eventually broken into separate files, and then finally systemized under a uniform caller that developed into a system of packages. This natural evolution has made keeping every script uniform a large task in itself for a single developer.",
  },
  "functionalStreams": {
    "rationale": "Commonly I want to manipulate Javascript arrays as a pipeline composed from units of functionality, such as delaying, mapping, conditionally returning, etc., and whilst libraries such as RxJS provide a wonderful experience, their generality can become complicated.",
    "brief": "A solution to the above would be a simple and minimal library that operates over an iterable, applying an ordered set of operations to each element.",
    "problems": "No significant problems were present for achieving the functionality.",
    "solution": "The `functional-streams` package is a small, simple utility library that provides a select set of functional units that I most commonly want to use, wrapped by a `pipeline` function which returns the results and functionality for cancellation. The methods are subjectively simpler for me to understand than RxJS, so it has achieved its purpose.",
    "issues": "None, yet!",
    "Improvements": "It would be interesting to provide functionality to pause and resume, but this hasn't been something I've required yet. This could be achieved by >>> TODO: paste notes from comments <<<",
  },
  "geoguesser": {
    "media": [
      {
        description: "Setting up the game with player names",
        format: "png",
        path: "/geoguesser/names.png",
        type: "image",
      },
      {
        description: "Inputting the geocoordinates of the game locations",
        format: "png",
        path: "/geoguesser/latlng-ui.png",
        type: "image",
      },
      {
        description: "Inputting the geocoordinates of the game locations",
        format: "png",
        path: "/geoguesser/latlng.png",
        type: "image",
      },
      {
        description: "Results from one round of the game",
        format: "png",
        path: "/geoguesser/results-discord.png",
        type: "image",
      },
      {
        description: "Final results from our game. Note the wonderfully dressed people!",
        format: "png",
        path: "/geoguesser/final-results.png",
        type: "image",
      },
    ],
    "thumbnail": "/geoguesser/final-results.thumbnail.png",
    "rationale": "Geoguesser is a fun game that me and my friends have enjoyed, and this has combined with my love for creating problems to solve into my wanting to design a similar game.",
    "brief": "A solution to create this game is a browser extension that augments Google Maps to remove identifiable features of the current location, allow input for location guesses per player, calculate a relative (to half the Earth's radius) distance, and total the score for ranking the accuracy of the player's score.",
    "problems": "No significant problems were present for achieving the functionality.",
    "solution": "The extension provides an interface to toggle game augmentation of Google Maps. Once activated, the game allows for input of players, and the coordinates to Google Street View locations. Once the game has begun, the users are given a predetermined amount of time to input their guesses per location, and the game either automatically moves onto the next Street View location after the alloted time or once the player terminates the current location early. Per round, the user will be notified of the location, and shown their relative distance between their guess and the actual location, normalised by division of half the circumference of the Earth (~20Mm), subtracted by 1 (e.g., if the difference of distance is 1000km, then their score is 1 - [1Mm / 20Mm] = 0.95). After all location rounds are displayed, the game displays the total score of the users as a table.",
    "issues": "The game has bugs that directly conflict with the amount of energy the developer has to solve them.",
  },
  "gantt": {
    "example": <GanttApp />,
    "thumbnail": "/gantt/gantt.thumbnail.png",
    "rationale": "Gantt charts are useful for many information systems, and have cropped up in a few projects, both personal and professional, and so I desired a beautiful and reusable component for their implementation.",
    "brief": "A solution would be a configurable React component.",
    "problems": "No significant problems were present for achieving the functionality.",
    "solution": "Using SVG components, it was simple to create a configurable component with moveable items, dependency arrows and click events, which are the most common functionalities I have required for this component.",
    "issues": "None yet!",
  },
  "plantPot": {
    "media": [
      {
        description: "The weight of these plant pots bowed the fence! I had to lay concrete and add metal post feet to support the base",
        format: "jpg",
        path: "/plant-pot/fence.jpg",
        type: "image",
      },
      {
        description: "More exercise in one day than I'd done in a decade",
        format: "jpg",
        path: "/plant-pot/saw.jpg",
        type: "image",
      },
      {
        description: "Clamping glued feet to prop up the base, so that the weight isn't supported by the plastic window ledge",
        format: "jpg",
        path: "/plant-pot/window-clamp.jpg",
        type: "image",
      },
      {
        description: "Final result of the plant pot at the window",
        format: "jpg",
        path: "/plant-pot/window.jpg",
        type: "image",
      },
    ],
    "thumbnail": "/plant-pot/fence.thumbnail.jpg",
    "rationale": "I like plants and woodwork, and I don't have enough dirt to put them in. True synergy!",
    "brief": "A solution is to stuff as many plant pots as possible into my tiny studio flat terrace without annoying my partner, in particular out of scrap wood where possible.",
    "problems": "Breaking up a wooden pallet requires the determination of a hungry bear with a bee hive.",
    "solution": "Create plant pots from scrap wood pallets that I found around my estate, and where necessary from cheap timber. ",
    "issues": "Woodwork is surprisingly exertive and makes me conscious of how unfit I am. My capacity to wing the design caused me a ton of issues, so future advice to myself would be to plan the structure ahead of time.",
  },
};

export const articleService = {
  getArticles: () => articles,
  getArticle: (articleIndex) => articles[articleIndex],
};
